import { Fragment, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Controller, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { FiPlus } from "react-icons/fi";
import Card from "../../components/Card";
import { setInterface } from "../../redux/slices/interfaceSlice";
import service from "../../service";
import ReactSelect from "react-select";
import swal from "@sweetalert/with-react";
import BigLoader from "../../components/BigLoader";
import Button from "../../components/Button";
import Textarea from "../../components/Textarea";
import TextInput from "../../components/TextInput";

export default function Config() {
  const dispatch = useDispatch();
  const [categories, setCategories] = useState([]);
  const { handleSubmit, register, reset, control } = useForm();
  const [loading, setLoading] = useState(false);
  const [banners, setBanners] = useState([]);
  const [newBannerFile, setNewBannerFile] = useState(null);
  const [newBannerUrl, setNewBannerUrl] = useState("");
  const [newBannerPreview, setNewBannerPreview] = useState("");
  const [editingBanner, setEditingBanner] = useState(null);
  const [qrisPreview, setQrisPreview] = useState("");
  const [qrisFile, setQrisFile] = useState(null);

  const parsingSelect = (value = [], options = []) => {
    const values = [];
    for (let selected of value) {
      const option = options.find((el) => el.value === selected);
      if (option) {
        values.push(option);
      }
    }

    return values;
  };

  const _proceed = (data) => {
    setLoading(true);
    service
      .post("/api/config", data)
      .then(() => {
        setLoading(false);
        swal("Berhasil", "Pengaturan telah disimpan", "success");
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const getConfig = () => {
    service
      .get("/api/config")
      .then((response) => {
        const { stickyCategory, paymentText } = response.data;

        reset({
          stickyCategory,
          paymentText,
        });
      })
      .catch(() => {});
  };

  const getBanner = () => {
    service
      .get("/api/config/banner")
      .then((response) => {
        setBanners(response.data);
      })
      .catch(() => {});
  };

  const submitNewBanner = (e) => {
    e.preventDefault();
    if (!newBannerFile) {
      swal("Error", "File banner harus dipilih", "error");
      return;
    }
    if (!newBannerUrl) {
      swal("Error", "URL tujuan harus diisi", "error");
      return;
    }
    setLoading(true);
    const formData = new FormData();
    formData.append("url", newBannerUrl);
    formData.append("banner", newBannerFile);
    service
      .post("/api/config/banner", formData)
      .then((response) => {
        setLoading(false);
        swal("Berhasil", "Banner telah ditambahkan", "success");
        setBanners(response.data);
        setNewBannerFile(null);
        setNewBannerUrl("");
        setNewBannerPreview("");
      })
      .catch((err) => {
        setLoading(false);
        const msg = err.response?.data?.error || "Gagal menambahkan banner";
        swal("Error", msg, "error");
      });
  };

  const removeBanner = (index) => {
    swal({
      title: "Hapus Banner?",
      icon: "warning",
      buttons: ["Batal", "Hapus"],
      dangerMode: true,
    }).then((confirmed) => {
      if (!confirmed) return;
      setLoading(true);
      service
        .delete(`/api/config/banner/${index}`)
        .then((response) => {
          setLoading(false);
          swal("Berhasil", "Banner telah dihapus", "success");
          setBanners(response.data);
        })
        .catch(() => {
          setLoading(false);
        });
    });
  };

  const startEditBanner = (index) => {
    setEditingBanner({ index, url: banners[index].url, file: null, preview: "" });
  };

  const cancelEditBanner = () => {
    setEditingBanner(null);
  };

  const submitEditBanner = (e) => {
    e.preventDefault();
    if (!editingBanner) return;
    setLoading(true);
    const formData = new FormData();
    formData.append("url", editingBanner.url);
    if (editingBanner.file) {
      formData.append("banner", editingBanner.file);
    }
    service
      .put(`/api/config/banner/${editingBanner.index}`, formData)
      .then((response) => {
        setLoading(false);
        swal("Berhasil", "Banner telah diperbarui", "success");
        setBanners(response.data);
        setEditingBanner(null);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const getQris = () => {
    service
      .get("/api/config/qris")
      .then((response) => {
        const { image } = response.data;
        if (image) {
          setQrisPreview(
            image.startsWith("http")
              ? image
              : `${process.env.REACT_APP_BASE_URL}/${image}`
          );
        } else {
          setQrisPreview("");
        }
      })
      .catch(() => {});
  };

  const submitQris = (e) => {
    e.preventDefault();
    if (!qrisFile) return;
    setLoading(true);
    const formData = new FormData();
    formData.append("qris", qrisFile);
    service
      .post("/api/config/qris", formData)
      .then(() => {
        setLoading(false);
        swal("Berhasil", "QRIS telah disimpan", "success");
        setQrisPreview(URL.createObjectURL(qrisFile));
        setQrisFile(null);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    dispatch(setInterface({ active: "Settings", pageTitle: "Settings" }));
  }, [dispatch]);

  useEffect(() => {
    service
      .get("/api/category")
      .then((response) => {
        const data = [];
        for (let category of response.data) {
          data.push({
            value: category.id,
            label: category.name,
          });
        }

        setCategories(data);
      })
      .catch((e) => {});
  }, []);

  useEffect(() => {
    getConfig();
    getBanner();
    getQris();
  }, [categories]);

  return (
    <Fragment>
      <Helmet>
        <title>Settings</title>
      </Helmet>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card>
          <form className="w-full" onSubmit={handleSubmit(_proceed)}>
            <div className="mb-4">
              <label className="text-black-200 font-semibold text-sm mb-1 block">
                Sticky Kategori
              </label>
              <Controller
                name="stickyCategory"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <ReactSelect
                    isMulti
                    styles={{
                      control: (base) => ({
                        ...base,
                        border: 0,
                        boxShadow: "none",
                      }),
                    }}
                    value={parsingSelect(value, categories)}
                    onChange={(inputValue) => {
                      const values = [];
                      for (let value of inputValue) {
                        values.push(value.value);
                      }

                      onChange(values);
                    }}
                    options={categories}
                    classNamePrefix="react-select"
                    className="react-select-container mb-4"
                  />
                )}
              />
            </div>
            <Textarea
              type="text"
              label="Teks Pembayaran"
              containerClassName="mb-4"
              rows={8}
              {...register("paymentText")}
            />
            <Button type="submit" className="w-24">
              Simpan
            </Button>
          </form>
        </Card>
        <Card>
          <div className="mb-4">
            <label className="text-black-200 font-semibold text-sm mb-1 block">
              Banner Home (rasio 1:4, maksimal 5)
            </label>

            {banners.length > 0 && (
              <div className="mt-4 space-y-4">
                {banners.map((banner, index) => (
                  <div
                    key={index}
                    className="border border-white-300 p-3 rounded-sm"
                  >
                    {editingBanner?.index === index ? (
                      <form onSubmit={submitEditBanner}>
                        <div className="relative w-full" style={{ paddingTop: "25%" }}>
                          <img
                            src={
                              editingBanner.preview ||
                              `${process.env.REACT_APP_BASE_URL}/api/stream/banner/${index}`
                            }
                            alt={`Banner ${index + 1}`}
                            className="absolute inset-0 w-full h-full object-cover rounded"
                          />
                        </div>
                        <input
                          type="file"
                          accept=".jpg,.jpeg,.png,.webp"
                          className="block mt-2 text-sm text-black-300 file:mr-3 file:py-2 file:px-3 file:rounded-sm file:border-0 file:bg-primary-50 file:text-primary-600 file:font-semibold hover:file:bg-primary-100 file:cursor-pointer cursor-pointer"
                          onChange={(e) => {
                            if (e.target.files.length > 0) {
                              setEditingBanner((prev) => ({
                                ...prev,
                                file: e.target.files[0],
                                preview: URL.createObjectURL(e.target.files[0]),
                              }));
                            }
                          }}
                        />
                        <TextInput
                          label="URL Tujuan"
                          type="text"
                          value={editingBanner.url}
                          onChange={(e) =>
                            setEditingBanner((prev) => ({ ...prev, url: e.target.value }))
                          }
                          containerClassName="mb-2 mt-2"
                        />
                        <div className="flex gap-2">
                          <Button type="submit" className="w-24">
                            Simpan
                          </Button>
                          <Button type="button" color="white" onClick={cancelEditBanner}>
                            Batal
                          </Button>
                        </div>
                      </form>
                    ) : (
                      <Fragment>
                        <div className="relative w-full" style={{ paddingTop: "25%" }}>
                          <img
                            src={`${process.env.REACT_APP_BASE_URL}/api/stream/banner/${index}`}
                            alt={`Banner ${index + 1}`}
                            className="absolute inset-0 w-full h-full object-cover rounded"
                          />
                        </div>
                        <p className="mt-2 text-sm text-black-300">
                          URL: {banner.url || "-"}
                        </p>
                        <div className="flex gap-2 mt-2">
                          <Button
                            type="button"
                            onClick={() => startEditBanner(index)}
                          >
                            Edit
                          </Button>
                          <Button
                            type="button"
                            color="red"
                            onClick={() => removeBanner(index)}
                          >
                            Hapus
                          </Button>
                        </div>
                      </Fragment>
                    )}
                  </div>
                ))}
              </div>
            )}

            {banners.length < 5 && !editingBanner && (
              <form onSubmit={submitNewBanner} className="mt-4">
                <hr className="mb-4 border-white-200" />
                <label className="font-semibold text-sm">
                  Tambah Banner Baru ({banners.length}/5)
                </label>
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp"
                  className="block mt-2 text-sm text-black-300 file:mr-3 file:py-2 file:px-3 file:rounded-sm file:border-0 file:bg-primary-50 file:text-primary-600 file:font-semibold hover:file:bg-primary-100 file:cursor-pointer cursor-pointer"
                  onChange={(e) => {
                    if (e.target.files.length > 0) {
                      setNewBannerFile(e.target.files[0]);
                      setNewBannerPreview(URL.createObjectURL(e.target.files[0]));
                    }
                  }}
                />
                {newBannerPreview ? (
                  <div className="relative w-full mt-4" style={{ paddingTop: "25%" }}>
                    <img
                      src={newBannerPreview}
                      alt="Preview"
                      className="absolute inset-0 w-full h-full object-cover rounded"
                    />
                  </div>
                ) : null}
                <TextInput
                  label="URL Tujuan"
                  type="text"
                  value={newBannerUrl}
                  onChange={(e) => setNewBannerUrl(e.target.value)}
                  containerClassName="mb-4 mt-2"
                />
                <Button type="submit" color="green">
                  <FiPlus className="mr-1" /> Tambah Banner
                </Button>
              </form>
            )}
          </div>
        </Card>
        <Card>
          <form onSubmit={submitQris}>
            <div className="mb-4">
              <label className="text-black-200 font-semibold text-sm mb-1 block">QRIS</label>
              <input
                type="file"
                accept=".jpg,.jpeg,.png,.webp"
                className="block mt-2 text-sm text-black-300 file:mr-3 file:py-2 file:px-3 file:rounded-sm file:border-0 file:bg-primary-50 file:text-primary-600 file:font-semibold hover:file:bg-primary-100 file:cursor-pointer cursor-pointer"
                onChange={(e) => {
                  if (e.target.files.length > 0) {
                    setQrisFile(e.target.files[0]);
                    setQrisPreview(URL.createObjectURL(e.target.files[0]));
                  }
                }}
              />
              {qrisPreview ? (
                <div className="relative w-48 h-48 mt-4 mx-auto">
                  <img
                    src={qrisPreview}
                    alt="QRIS"
                    className="absolute inset-0 w-full h-full object-contain rounded"
                  />
                </div>
              ) : null}
            </div>
            <Button type="submit" className="w-24">
              Simpan
            </Button>
          </form>
        </Card>
      </div>
      {loading && <BigLoader />}
    </Fragment>
  );
}